import { patientBffApi } from '@/modules/baseApi';
import { UserConfigService } from './userConfig.service';
import { unwrapApiErrorDetails } from '@ianesthesie-kit/services';
import { UserConfig } from './userConfig.model';
import { ApiLanguage } from '@ianesthesie-kit/config';
import { useActiveSurgicalEvent } from '@/modules/surgicalEvent';
import { pingCurrentUser } from '@ianesthesie-kit/redux';

export const userConfigApi = patientBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    userConfig: builder.query<UserConfig, void>({
      providesTags: ['UserConfig'],
      queryFn: () =>
        UserConfigService.getInstance()
          .fetch()
          .then((r) => ({ data: r.data }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          }))
    }),

    changeLanguage: builder.mutation<UserConfig, ApiLanguage>({
      invalidatesTags: ['UserConfig'],
      queryFn: (payload) =>
        UserConfigService.getInstance()
          .changeLanguage(payload)
          .then((r) => ({ data: r.data }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          }))
    }),

    toggleMarketingCommsStatus: builder.mutation<UserConfig, void>({
      queryFn: () =>
        UserConfigService.getInstance()
          .toggleMarketingCommsStatus()
          .then((r) => ({ data: r.data }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          })),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        let optimisticUpdateDone = false;
        const patchResult = dispatch(
          userConfigApi.util.updateQueryData('userConfig', _, (draft) => {
            if (draft) {
              optimisticUpdateDone = true;
              draft.marketingCommsStatus = !draft.marketingCommsStatus;
            }
          })
        );
        try {
          const { data } = await queryFulfilled;
          if (!optimisticUpdateDone) {
            // In case the optimistic update did not happen (e.g. because the userConfig query was not in cache), we update the cache with the actual result
            dispatch(userConfigApi.util.upsertQueryData('userConfig', _, data));
          }
        } catch {
          patchResult.undo();
        }
      }
    }),

    toggleClinicalTrialOptOut: builder.mutation<void, void>({
      queryFn: () =>
        UserConfigService.getInstance()
          .toggleClimicalTrialOptOut()
          .then((r) => ({ data: r.data }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          })),
      invalidatesTags: ['ActiveSurgicalEvent']
    }),

    optOutEmailCommunication: builder.mutation<void, void>({
      queryFn: () =>
        UserConfigService.getInstance()
          .optOutEmailCommunication()
          .then((r) => ({ data: r.data }))
          .catch((err) => ({
            error: unwrapApiErrorDetails(err) || err
          })),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(pingCurrentUser());
        } catch {
          // nothing
        }
      }
    })
  })
});

export const {
  useUserConfigQuery,
  useChangeLanguageMutation,
  useToggleMarketingCommsStatusMutation,
  useToggleClinicalTrialOptOutMutation,
  useOptOutEmailCommunicationMutation
} = userConfigApi;

export const useIsClinicalTrialOptOut = () =>
  useActiveSurgicalEvent().currentData?.patient.clinicalTrialOptOut;
